import * as React from "react";
import { useTranslation } from "react-i18next";
import { CheckCheck, Pencil } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNewTaskStatusMutations } from "../hooks";
import type { Task } from "../types";

interface FinishAdjustmentDialogProps {
  /** The adjustment task itself (task.isAdjustment === true) */
  task: Task;
  /** When set, the button is disabled and this string is shown as a tooltip */
  disabledReason?: string;
  /**
   * "finish" (default) records the result for the first time; "edit" lets the
   * owner change the already-recorded result (percent + comment) — used on
   * approved/billed (terminal) tasks. Edit mode pre-fills the existing values.
   */
  mode?: "finish" | "edit";
  /** Pre-fill value for the percent input (edit mode). */
  initialPercent?: number;
  /** Pre-fill value for the comment input (edit mode). */
  initialComment?: string;
  onSuccess?: () => void;
}

export function FinishAdjustmentDialog({
  task,
  disabledReason,
  mode = "finish",
  initialPercent,
  initialComment,
  onSuccess,
}: FinishAdjustmentDialogProps) {
  const isEdit = mode === "edit";
  const defaultPercent = initialPercent ?? 10;
  const [open, setOpen] = React.useState(false);
  const [adjustmentPercent, setAdjustmentPercent] = React.useState(defaultPercent);
  const [comment, setComment] = React.useState(initialComment ?? "");
  const { t } = useTranslation();
  const { completeAdjustmentMutation } = useNewTaskStatusMutations();

  // Reset state when dialog opens — pre-fill from the current result in edit mode.
  React.useEffect(() => {
    if (open) {
      setAdjustmentPercent(defaultPercent);
      setComment(initialComment ?? "");
    }
  }, [open, defaultPercent, initialComment]);

  const handlePercentChange = (value: string) => {
    const normalized = value.replace(/^0+(?=\d)/, "");
    const num = parseInt(normalized, 10);
    if (isNaN(num)) {
      setAdjustmentPercent(0);
      return;
    }
    setAdjustmentPercent(Math.min(100, Math.max(0, num)));
  };

  const handleSubmit = () => {
    completeAdjustmentMutation.mutate(
      {
        adjustmentId: task.id,
        data: {
          adjustmentPercent,
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        },
      },
      {
        onSuccess: () => {
          setOpen(false);
          onSuccess?.();
        },
      },
    );
  };

  const triggerButton = isEdit ? (
    <Button size="sm" variant="outline" disabled={!!disabledReason}>
      <Pencil className="h-4 w-4" />
      {t("adjustment.editResult")}
    </Button>
  ) : (
    <Button
      size="sm"
      disabled={!!disabledReason}
      className="bg-blue-600 hover:bg-blue-700 text-white"
    >
      <CheckCheck className="h-4 w-4" />
      {t("adjustment.finish")}
    </Button>
  );

  if (disabledReason) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex">
              {triggerButton}
            </span>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{triggerButton}</DialogTrigger>

      <DialogContent className="!flex !flex-col sm:max-w-lg p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            {isEdit ? (
              <Pencil className="h-5 w-5" />
            ) : (
              <CheckCheck className="h-5 w-5" />
            )}
            {isEdit ? t("adjustment.editTitle") : t("adjustment.finishTitle")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {isEdit
              ? t("adjustment.editDescription")
              : t("adjustment.dialogDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 space-y-4">
          {/* Percentage input — clamped 0-100 */}
          <div className="space-y-2">
            <Label htmlFor="finish-adjustment-percent" className="text-sm font-medium">
              {t("adjustment.percentLabel")}
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("adjustment.percentHelp")}
            </p>
            <div className="flex items-center gap-2">
              <Input
                id="finish-adjustment-percent"
                type="number"
                min={0}
                max={100}
                value={adjustmentPercent}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const normalized = rawValue.replace(/^0+(?=\d)/, "");
                  if (normalized !== rawValue) {
                    e.target.value = normalized;
                  }
                  handlePercentChange(normalized);
                }}
                className="w-24 h-8 text-center text-sm"
              />
              <span className="text-sm text-muted-foreground">/ 100%</span>
            </div>
          </div>

          {/* Optional comment */}
          <div className="space-y-2">
            <Label htmlFor="finish-adjustment-comment" className="text-sm font-medium">
              {t("comment")}
              <span className="text-muted-foreground ml-1 text-xs">
                ({t("optional")})
              </span>
            </Label>
            <Textarea
              id="finish-adjustment-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("enterComment")}
              className="resize-none"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("cancel")}
            </Button>
          </DialogClose>
          <Button
            onClick={handleSubmit}
            disabled={completeAdjustmentMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {completeAdjustmentMutation.isPending
              ? t("saving")
              : isEdit
                ? t("save")
                : t("adjustment.finish")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
